"use client"

import React, { useEffect, useState } from "react"
import { css } from "@emotion/react"
import Image from "next/image"
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  useCarousel,
} from "@/components/ui/carousel-pro"
import styled from "@emotion/styled"
import { placeHolderImage } from "@/constants/placeHolderImage"

const StyledSlideBtn = styled.button<{
  position: "top" | "bottom"
  visible: boolean
}>`
  position: absolute;
  left: 0;
  top: ${(props) => (props.position === "top" ? 0 : "auto")};
  bottom: ${(props) => (props.position === "bottom" ? 0 : "auto")};
  width: 100%;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  display: ${(props) => (props.visible ? "flex" : "none")};
  border: none;
  outline: none;
  padding: 0;
  background-color: rgba(255, 255, 255, 0.5);
  z-index: 2;
  cursor: pointer;
`

const CarouselSlideBtn = () => {
  const { canScrollNext, canScrollPrev, scrollNext, scrollPrev } = useCarousel()

  return (
    <>
      <StyledSlideBtn
        type="button"
        onClick={scrollPrev}
        position="top"
        visible={canScrollPrev}
      >
        <div className="h-[11px] w-[20px]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="11"
            fill="none"
            viewBox="0 0 20 11"
          >
            <g clipPath="url(#clip0_7579_20982)">
              <path
                stroke="#4B5563"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18.438 9.438 10 1 1.563 9.438"
              ></path>
            </g>
            <defs>
              <clipPath id="clip0_7579_20982">
                <path fill="#fff" d="M19.438 0v10.438H.563V0z"></path>
              </clipPath>
            </defs>
          </svg>
        </div>
      </StyledSlideBtn>
      <StyledSlideBtn
        type="button"
        onClick={scrollNext}
        position="bottom"
        visible={canScrollNext}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="11"
          fill="none"
          viewBox="0 0 20 11"
        >
          <g clipPath="url(#clip0_7579_11205)">
            <path
              stroke="#4B5563"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M1.563 1.438 10 9.875l8.438-8.437"
            ></path>
          </g>
          <defs>
            <clipPath id="clip0_7579_11205">
              <path fill="#fff" d="M.563 10.875V.437h18.875v10.438z"></path>
            </clipPath>
          </defs>
        </svg>
      </StyledSlideBtn>
    </>
  )
}

interface Props {
  images: any[]
  activeIndex: number
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>
  videoUrl: string
  productName: string
}

const ProductThumbnails = (props: Props) => {
  const { images, activeIndex, setActiveIndex, videoUrl } = props

  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null)

  useEffect(() => {
    if (!carouselApi) {
      return
    }

    carouselApi?.scrollTo(activeIndex, false)
  }, [activeIndex, carouselApi])

  return (
    <div className="text-0 relative">
      <Carousel
        opts={{
          align: "start",
        }}
        orientation="vertical"
        setApi={setCarouselApi}
        autoPlayOpts={{ playOnInit: false }}
      >
        <CarouselSlideBtn />
        <CarouselContent
          style={{
            display: "flex",
            flexDirection: "column",
            width: 120,
            height: 710,
            margin: "-5px 0 0",
          }}
        >
          {images.reduce((acc, item, index, arr) => {
            const imageAlt = [`Image${index + 1}`].filter(Boolean).join(" ")

            const isActive =
              (index < 1 || !videoUrl ? activeIndex : activeIndex - 1) === index

            acc.push(
              <CarouselItem
                key={index}
                style={{
                  flex: "0 0 120px",
                  marginBlock: "10px",
                }}
              >
                <div
                  className={`relative box-border cursor-pointer border-1 ${isActive ? "bordder-[#000]" : "border-transparent"}`}
                  onClick={() => {
                    setActiveIndex(!videoUrl || index < 1 ? index : index + 1)
                  }}
                >
                  <Image
                    src={item.image}
                    width={1500}
                    height={1500}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    alt={imageAlt}
                    title={imageAlt}
                    placeholder={placeHolderImage}
                    loader={({ src }) => src}
                    unoptimized
                  />
                </div>
              </CarouselItem>
            )

            if (index === 0 && videoUrl) {
              const firstImageAlt = [`Image1`].filter(Boolean).join(" ")

              acc.push(
                <CarouselItem
                  key={arr.length}
                  style={{
                    flex: "0 0 120px",
                  }}
                >
                  <div
                    className={`relative cursor-pointer border-1 ${activeIndex === 1 ? "border-[#000]" : "border-transparent"}`}
                    onClick={() => {
                      setActiveIndex(1)
                    }}
                  >
                    <div
                      style={{
                        backdropFilter: "blur(2px)",
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 1,
                      }}
                    />

                    <Image
                      src={arr[0].image}
                      width={1500}
                      height={1500}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      alt={firstImageAlt}
                      title={firstImageAlt}
                      placeholder={placeHolderImage}
                      loader={({ src }) => src}
                      unoptimized
                    />
                    {videoUrl ? (
                      <div className="absolute left-0 top-0 z-[2] flex h-full w-full items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="50"
                          height="50"
                          fill="none"
                          viewBox="0 0 50 50"
                        >
                          <circle
                            cx="25"
                            cy="25"
                            r="24"
                            stroke="#fff"
                            strokeWidth="2"
                          ></circle>
                          <path
                            fill="#fff"
                            stroke="#fff"
                            d="M33.75 23.701c1 .577 1 2.02 0 2.598l-12 6.928a1.5 1.5 0 0 1-2.25-1.299V18.072a1.5 1.5 0 0 1 2.25-1.3z"
                          ></path>
                        </svg>
                      </div>
                    ) : null}
                  </div>
                </CarouselItem>
              )
            }

            return acc
          }, [])}
        </CarouselContent>
      </Carousel>
    </div>
  )
}

export default ProductThumbnails
